import React from "react";
import { CasesStatistics } from "../../models/Statistics/CasesStatistics";
import StatisticsCard from "../common/StatisticsCard";
import { CHART_COLORS } from "../../constants/ChartColors";

interface CasesStatisticsCardProps {
  statistics: CasesStatistics;
  displayOpenCaseStatistics: boolean;
  displayClosedCaseStatistics: boolean;
}

const CasesStatisticsCard: React.FC<CasesStatisticsCardProps> = ({
  statistics,
  displayOpenCaseStatistics,
  displayClosedCaseStatistics
}) => {
  let numberOfCases : number = statistics.numberOfCaseFolders;
  if (displayOpenCaseStatistics) numberOfCases = numberOfCases + statistics.numberOfOpenCases;
  if (displayClosedCaseStatistics) numberOfCases = numberOfCases + statistics.numberOfClosedCases;

  let casesMigrated : number = statistics.caseFoldersMigrated;
  if (displayOpenCaseStatistics) casesMigrated = casesMigrated + statistics.openCasesMigrated;
  if (displayClosedCaseStatistics) casesMigrated = casesMigrated + statistics.closedCasesMigrated;

  let casesInProgress : number = statistics.caseFoldersInProgress;
  if (displayOpenCaseStatistics) casesInProgress = casesInProgress + statistics.openCasesInProgress;
  if (displayClosedCaseStatistics) casesInProgress = casesInProgress + statistics.closedCasesInProgress;

  let casesInterrupted : number = statistics.caseFoldersInterrupted;
  if (displayOpenCaseStatistics) casesInterrupted = casesInterrupted + statistics.openCasesInterrupted;
  if (displayClosedCaseStatistics) casesInterrupted = casesInterrupted + statistics.closedCasesInterrupted;

  let casesWithFlaggedStatus : number = statistics.caseFoldersFlagged;
  if (displayOpenCaseStatistics) casesWithFlaggedStatus = casesWithFlaggedStatus + statistics.openCasesFlagged;
  if (displayClosedCaseStatistics) casesWithFlaggedStatus = casesWithFlaggedStatus + statistics.closedCasesFlagged;

  let casesResolved : number = statistics.caseFoldersResolved;
  if (displayOpenCaseStatistics) casesResolved = casesResolved + statistics.openCasesResolved;
  if (displayClosedCaseStatistics) casesResolved = casesResolved + statistics.closedCasesResolved;

  let casesAcknowledged : number = statistics.caseFoldersAcknowledged;
  if (displayOpenCaseStatistics) casesAcknowledged = casesAcknowledged + statistics.openCasesAcknowledged;
  if (displayClosedCaseStatistics) casesAcknowledged = casesAcknowledged + statistics.closedCasesAcknowledged;

  const casesNotStarted = numberOfCases - (casesMigrated + casesInProgress + casesInterrupted + casesWithFlaggedStatus);
  const casesFlagged = casesWithFlaggedStatus - (casesAcknowledged + casesResolved);

  const caseChartData = [
    {
      name: "Migrated",
      value: casesMigrated,
      color: CHART_COLORS.migrated,
    },
    {
      name: "In Progress",
      value: casesInProgress,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Interrupted",
      value: casesInterrupted,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Not Started",
      value: casesNotStarted,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Flagged",
      value: casesFlagged,
      color: CHART_COLORS.flagged,
    },
    {
      name: "Allowed",
      value: casesResolved,
      color: CHART_COLORS.allowed,
    },
    {
      name: "Acknowledged",
      value: casesAcknowledged,
      color: CHART_COLORS.acknowledged,
    },
  ];

  let totalNumberOfFiles : number = statistics.totalNumberOfCaseFolderFiles;
  if (displayOpenCaseStatistics) totalNumberOfFiles = totalNumberOfFiles + statistics.totalNumberOfOpenCaseFiles;
  if (displayClosedCaseStatistics) totalNumberOfFiles = totalNumberOfFiles + statistics.totalNumberOfClosedCaseFiles;

  let filesMigrated : number = statistics.caseFolderFilesMigrated;
  if (displayOpenCaseStatistics) filesMigrated = filesMigrated + statistics.openCaseFilesMigrated;
  if (displayClosedCaseStatistics) filesMigrated = filesMigrated + statistics.closedCaseFilesMigrated;

  let filesNotFound : number = statistics.caseFolderFilesNotFound;
  if (displayOpenCaseStatistics) filesNotFound = filesNotFound + statistics.openCaseFilesNotFound;
  if (displayClosedCaseStatistics) filesNotFound = filesNotFound + statistics.closedCaseFilesNotFound;

  let filesNotFoundMarkedForRetry : number = statistics.caseFolderFilesNotFoundMarkedForRetry;
  if (displayOpenCaseStatistics) filesNotFoundMarkedForRetry = filesNotFoundMarkedForRetry + statistics.openCaseFilesNotFoundMarkedForRetry;
  if (displayClosedCaseStatistics) filesNotFoundMarkedForRetry = filesNotFoundMarkedForRetry + statistics.closedCaseFilesNotFoundMarkedForRetry;

  let filesInterruptedInitial : number = statistics.caseFolderFilesInterruptedInitial;
  if (displayOpenCaseStatistics) filesInterruptedInitial = filesInterruptedInitial + statistics.openCaseFilesInterruptedInitial;
  if (displayClosedCaseStatistics) filesInterruptedInitial = filesInterruptedInitial + statistics.closedCaseFilesInterruptedInitial;

  let filesInterruptedDelta : number = statistics.caseFolderFilesInterruptedDelta;
  if (displayOpenCaseStatistics) filesInterruptedDelta = filesInterruptedDelta + statistics.openCaseFilesInterruptedDelta;
  if (displayClosedCaseStatistics) filesInterruptedDelta = filesInterruptedDelta + statistics.closedCaseFilesInterruptedDelta;

  let filesModified : number = statistics.caseFolderFilesModified;
  if (displayOpenCaseStatistics) filesModified = filesModified + statistics.openCaseFilesModified;
  if (displayClosedCaseStatistics) filesModified = filesModified + statistics.closedCaseFilesModified;

  let filesFlagged : number = statistics.caseFolderFilesFlagged;
  if (displayOpenCaseStatistics) filesFlagged = filesFlagged + statistics.openCaseFilesFlagged;
  if (displayClosedCaseStatistics) filesFlagged = filesFlagged + statistics.closedCaseFilesFlagged;

  let duplicateFilesSkipped : number = statistics.caseFolderFilesSkippedDuplicate;
  if (displayOpenCaseStatistics) duplicateFilesSkipped = duplicateFilesSkipped + statistics.openCaseFilesSkippedDuplicate;
  if (displayClosedCaseStatistics) duplicateFilesSkipped = duplicateFilesSkipped + statistics.closedCaseFilesSkippedDuplicate;

  let filesBlockedByConflict : number = statistics.caseFilesInCaseFoldersUnresolvedConflictCount;
  if (displayOpenCaseStatistics) filesBlockedByConflict = filesBlockedByConflict + statistics.caseFilesInOpenCasesUnresolvedConflictCount;
  if (displayClosedCaseStatistics) filesBlockedByConflict = filesBlockedByConflict + statistics.caseFilesInClosedCasesUnresolvedConflictCount;

  let filesAcknowledgedConflict : number = statistics.caseFilesInCaseFoldersAcknowledgedConflictCount;
  if (displayOpenCaseStatistics) filesAcknowledgedConflict = filesAcknowledgedConflict + statistics.caseFilesInOpenCasesAcknowledgedConflictCount;
  if (displayClosedCaseStatistics) filesAcknowledgedConflict = filesAcknowledgedConflict + statistics.caseFilesInClosedCasesAcknowledgedConflictCount;

  // Conflicted files are blocked from migration, so they get their own chart buckets rather than the "Not Started" remainder.
  const filesNotStarted = Math.max(0, totalNumberOfFiles - (filesMigrated + filesInterruptedInitial + filesInterruptedDelta + filesModified + filesNotFound + filesNotFoundMarkedForRetry + filesFlagged + duplicateFilesSkipped + filesBlockedByConflict + filesAcknowledgedConflict));

  const filesChartData = [
    {
      name: "Migrated",
      value: filesMigrated,
      color: CHART_COLORS.migrated,
    },
    {
      name: "Not Found",
      value: filesNotFound,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Not Found (Marked For Retry)",
      value: filesNotFoundMarkedForRetry,
      color: CHART_COLORS.acknowledged,
    },
    {
      name: "Interrupted Initial",
      value: filesInterruptedInitial,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Interrupted Delta",
      value: filesInterruptedDelta,
      color: CHART_COLORS.interruptedDelta,
    },
    {
      name: "Modified",
      value: filesModified,
      color: CHART_COLORS.modified,
    },
    {
      name: "Not Started",
      value: filesNotStarted,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Blocked by conflict",
      value: filesBlockedByConflict,
      color: CHART_COLORS.blockedByConflict,
    },
    {
      name: "Acknowledged conflict",
      value: filesAcknowledgedConflict,
      color: CHART_COLORS.acknowledgedConflict,
    },
    {
      name: "Flagged",
      value: filesFlagged,
      color: CHART_COLORS.flagged,
    },
    {
      name: "Skipped Duplicate",
      value: duplicateFilesSkipped,
      color: CHART_COLORS.skippedDuplicate,
    },
  ];

  let dicomFilesFlagged : number = statistics.dicomCaseFolderFilesFlagged;
  if (displayOpenCaseStatistics) dicomFilesFlagged = dicomFilesFlagged + statistics.dicomOpenCaseFilesFlagged;
  if (displayClosedCaseStatistics) dicomFilesFlagged = dicomFilesFlagged + statistics.dicomClosedCaseFilesFlagged;

  let msgFilesFlagged : number = statistics.msgCaseFolderFilesFlagged;
  if (displayOpenCaseStatistics) msgFilesFlagged = msgFilesFlagged + statistics.msgOpenCaseFilesFlagged;
  if (displayClosedCaseStatistics) msgFilesFlagged = msgFilesFlagged + statistics.msgClosedCaseFilesFlagged;

  let filesInCasesWithOver10KFiles : number = statistics.caseFilesInCaseFoldersWithOver10KFiles;
  if (displayOpenCaseStatistics) filesInCasesWithOver10KFiles = filesInCasesWithOver10KFiles + statistics.caseFilesInOpenCasesWithOver10KFiles;
  if (displayClosedCaseStatistics) filesInCasesWithOver10KFiles = filesInCasesWithOver10KFiles + statistics.caseFilesInClosedCasesWithOver10KFiles;

  let filesInCasesBlockedByUser : number = statistics.caseFilesInCaseFoldersBlockedByUser;
  if (displayOpenCaseStatistics) filesInCasesBlockedByUser = filesInCasesBlockedByUser + statistics.caseFilesInOpenCasesBlockedByUser;
  if (displayClosedCaseStatistics) filesInCasesBlockedByUser = filesInCasesBlockedByUser + statistics.caseFilesInClosedCasesBlockedByUser;

  let filesMovedToFirmDocuments : number = statistics.caseFolderFilesMovedToFirmDocuments;
  if (displayOpenCaseStatistics) filesMovedToFirmDocuments = filesMovedToFirmDocuments + statistics.openCaseFilesMovedToFirmDocuments;
  if (displayClosedCaseStatistics) filesMovedToFirmDocuments = filesMovedToFirmDocuments + statistics.closedCaseFilesMovedToFirmDocuments;

  const flagsChartData = [
    {
      name: "DICOM",
      value: dicomFilesFlagged,
      color: CHART_COLORS.dicom,
    },
    {
      name: "Flagged Extensions",
      value: msgFilesFlagged,
      color: CHART_COLORS.flaggedExtensions,
    },
    {
      name: "Cases >10K",
      value: filesInCasesWithOver10KFiles,
      color: CHART_COLORS.over10k,
    },
    {
      name: "Blocked by user",
      value: filesInCasesBlockedByUser,
      color: CHART_COLORS.blockedByUser,
    },
    {
      name: "Moved To Firm Documents",
      value: filesMovedToFirmDocuments,
      color: CHART_COLORS.movedToFirmDocs,
    }
  ];

  let totalFilesSizeInGb : number = statistics.totalCaseFolderFilesSizeInGb;
  if (displayOpenCaseStatistics) totalFilesSizeInGb = totalFilesSizeInGb + statistics.totalOpenCaseFilesSizeInGb;
  if (displayClosedCaseStatistics) totalFilesSizeInGb = totalFilesSizeInGb + statistics.totalClosedCaseFilesSizeInGb;

  let totalMigratedFilesSizeInGb : number = statistics.totalMigratedCaseFolderFilesSizeInGb;
  if (displayOpenCaseStatistics) totalMigratedFilesSizeInGb = totalMigratedFilesSizeInGb + statistics.totalMigratedOpenCaseFilesSizeInGb;
  if (displayClosedCaseStatistics) totalMigratedFilesSizeInGb = totalMigratedFilesSizeInGb + statistics.totalMigratedClosedCaseFilesSizeInGb;

  let totalFilesSizeInMb : number = statistics.totalCaseFolderFilesSizeInMb;
  if (displayOpenCaseStatistics) totalFilesSizeInMb = totalFilesSizeInMb + statistics.totalOpenCaseFilesSizeInMb;
  if (displayClosedCaseStatistics) totalFilesSizeInMb = totalFilesSizeInMb + statistics.totalClosedCaseFilesSizeInMb;

  let totalMigratedFilesSizeInMb : number = statistics.totalMigratedCaseFolderFilesSizeInMb;
  if (displayOpenCaseStatistics) totalMigratedFilesSizeInMb = totalMigratedFilesSizeInMb + statistics.totalMigratedOpenCaseFilesSizeInMb;
  if (displayClosedCaseStatistics) totalMigratedFilesSizeInMb = totalMigratedFilesSizeInMb + statistics.totalMigratedClosedCaseFilesSizeInMb;

  let filesMovedToFirmDocumentsSizeInGb : number = statistics.caseFolderFilesMovedToFirmDocumentsSizeInGb;
  if (displayOpenCaseStatistics) filesMovedToFirmDocumentsSizeInGb = filesMovedToFirmDocumentsSizeInGb + statistics.openCaseFilesMovedToFirmDocumentsSizeInGb;
  if (displayClosedCaseStatistics) filesMovedToFirmDocumentsSizeInGb = filesMovedToFirmDocumentsSizeInGb + statistics.closedCaseFilesMovedToFirmDocumentsSizeInGb;

  let filesMovedToFirmDocumentsSizeInMb : number = statistics.caseFolderFilesMovedToFirmDocumentsSizeInMb;
  if (displayOpenCaseStatistics) filesMovedToFirmDocumentsSizeInMb = filesMovedToFirmDocumentsSizeInMb + statistics.openCaseFilesMovedToFirmDocumentsSizeInMb;
  if (displayClosedCaseStatistics) filesMovedToFirmDocumentsSizeInMb = filesMovedToFirmDocumentsSizeInMb + statistics.closedCaseFilesMovedToFirmDocumentsSizeInMb;

  let notResolvedFlaggedFilesSizeInGb : number = statistics.notResolvedFlaggedCaseFolderFilesSizeInGb;
  if (displayOpenCaseStatistics) notResolvedFlaggedFilesSizeInGb = notResolvedFlaggedFilesSizeInGb + statistics.notResolvedFlaggedOpenCaseFilesSizeInGb;
  if (displayClosedCaseStatistics) notResolvedFlaggedFilesSizeInGb = notResolvedFlaggedFilesSizeInGb + statistics.notResolvedFlaggedClosedCaseFilesSizeInGb;

  let notResolvedFlaggedFilesSizeInMb : number = statistics.notResolvedFlaggedCaseFolderFilesSizeInMb;
  if (displayOpenCaseStatistics) notResolvedFlaggedFilesSizeInMb = notResolvedFlaggedFilesSizeInMb + statistics.notResolvedFlaggedOpenCaseFilesSizeInMb;
  if (displayClosedCaseStatistics) notResolvedFlaggedFilesSizeInMb = notResolvedFlaggedFilesSizeInMb + statistics.notResolvedFlaggedClosedCaseFilesSizeInMb;

  let notFoundSizeInGb : number = statistics.notFoundCaseFolderFilesSizeInGb || 0;
  if (displayOpenCaseStatistics) notFoundSizeInGb += statistics.notFoundOpenCaseFilesSizeInGb || 0;
  if (displayClosedCaseStatistics) notFoundSizeInGb += statistics.notFoundClosedCaseFilesSizeInGb || 0;

  let notFoundSizeInMb : number = statistics.notFoundCaseFolderFilesSizeInMb || 0;
  if (displayOpenCaseStatistics) notFoundSizeInMb += statistics.notFoundOpenCaseFilesSizeInMb || 0;
  if (displayClosedCaseStatistics) notFoundSizeInMb += statistics.notFoundClosedCaseFilesSizeInMb || 0;

  let interruptedSizeInGb : number = (statistics.interruptedInitialCaseFolderFilesSizeInGb || 0) + (statistics.interruptedDeltaCaseFolderFilesSizeInGb || 0);
  if (displayOpenCaseStatistics) interruptedSizeInGb += (statistics.interruptedInitialOpenCaseFilesSizeInGb || 0) + (statistics.interruptedDeltaOpenCaseFilesSizeInGb || 0);
  if (displayClosedCaseStatistics) interruptedSizeInGb += (statistics.interruptedInitialClosedCaseFilesSizeInGb || 0) + (statistics.interruptedDeltaClosedCaseFilesSizeInGb || 0);

  let interruptedSizeInMb : number = (statistics.interruptedInitialCaseFolderFilesSizeInMb || 0) + (statistics.interruptedDeltaCaseFolderFilesSizeInMb || 0);
  if (displayOpenCaseStatistics) interruptedSizeInMb += (statistics.interruptedInitialOpenCaseFilesSizeInMb || 0) + (statistics.interruptedDeltaOpenCaseFilesSizeInMb || 0);
  if (displayClosedCaseStatistics) interruptedSizeInMb += (statistics.interruptedInitialClosedCaseFilesSizeInMb || 0) + (statistics.interruptedDeltaClosedCaseFilesSizeInMb || 0);

  let skippedDuplicateSizeInGb : number = statistics.skippedDuplicateCaseFolderFilesSizeInGb || 0;
  if (displayOpenCaseStatistics) skippedDuplicateSizeInGb += statistics.skippedDuplicateOpenCaseFilesSizeInGb || 0;
  if (displayClosedCaseStatistics) skippedDuplicateSizeInGb += statistics.skippedDuplicateClosedCaseFilesSizeInGb || 0;

  let skippedDuplicateSizeInMb : number = statistics.skippedDuplicateCaseFolderFilesSizeInMb || 0;
  if (displayOpenCaseStatistics) skippedDuplicateSizeInMb += statistics.skippedDuplicateOpenCaseFilesSizeInMb || 0;
  if (displayClosedCaseStatistics) skippedDuplicateSizeInMb += statistics.skippedDuplicateClosedCaseFilesSizeInMb || 0;

  return (
    <StatisticsCard
      migrationChartData={{ data: caseChartData, label: "Cases" }}
      filesChartData={{ data: filesChartData, label: "Files" }}
      fileFlagsChartData={{ data: flagsChartData, label: "Flags"}}
      title={"Cases Statistics"}
      totalFilesSizeInGb={totalFilesSizeInGb}
      totalMigratedFilesSizeInGb={totalMigratedFilesSizeInGb}
      totalFilesSizeInMb={totalFilesSizeInMb}
      totalMigratedFilesSizeInMb={totalMigratedFilesSizeInMb}
      totalFilesMovedToFirmDocsSizeInGb={filesMovedToFirmDocumentsSizeInGb}
      totalFilesMovedToFirmDocsSizeInMb={filesMovedToFirmDocumentsSizeInMb}
      notResolvedFlaggedFilesSizeInGb={notResolvedFlaggedFilesSizeInGb}
      notResolvedFlaggedFilesSizeInMb={notResolvedFlaggedFilesSizeInMb}
      notFoundFilesSizeInGb={notFoundSizeInGb}
      notFoundFilesSizeInMb={notFoundSizeInMb}
      interruptedFilesSizeInGb={interruptedSizeInGb}
      interruptedFilesSizeInMb={interruptedSizeInMb}
      skippedDuplicateFilesSizeInGb={skippedDuplicateSizeInGb}
      skippedDuplicateFilesSizeInMb={skippedDuplicateSizeInMb}
      showFlaggedSizeInfo={true}
    />
  );
};

export default CasesStatisticsCard;
